import axios from 'axios';
import { apiAddress } from '../../config';

/**
 * Get track metadata and audio URL
 * @param {string} trackId - Track UUID
 * @param {string} accessToken - JWT access token (required by backend)
 * @returns {Promise} Track data with presigned S3 URL
 */
export const getTrack = async (trackId, accessToken) => {
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await axios.get(`${apiAddress}/audio/tracks/${trackId}`, {
    headers,
  });
  return response.data;
};

/**
 * Start playback tracking (creates playback_event)
 * @param {string} trackId - Track UUID
 * @param {string} accessToken - JWT access token (required by backend)
 * @returns {Promise} Playback event ID for heartbeats
 */
export const startPlayback = async (trackId, accessToken) => {
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await axios.post(
    `${apiAddress}/audio/tracks/${trackId}/play`,
    { platform: 'web' },
    { headers }
  );
  return response.data;
};

/**
 * Send playback heartbeat (every 10 seconds)
 * @param {number} eventId - Playback event ID
 * @param {number} secondsListened - Cumulative seconds listened
 * @returns {Promise}
 */
export const sendHeartbeat = async (eventId, secondsListened, accessToken, percentageCompleted = null) => {
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const payload = { seconds_listened: secondsListened };
  if (percentageCompleted !== null && Number.isFinite(percentageCompleted)) {
    payload.percentage_completed = percentageCompleted;
  }

  const response = await axios.post(
    `${apiAddress}/audio/playback/${eventId}/heartbeat`,
    payload,
    { headers }
  );
  return response.data;
};

/**
 * Get all tracks for a tour
 * @param {string} tourId - Tour UUID
 * @returns {Promise} Array of tracks
 */
export const getTourTracks = async (tourId, accessToken = null) => {
  const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
  const response = await axios.get(`${apiAddress}/audio/tours/${tourId}/tracks`, { headers });
  return response.data;
};

/**
 * Update the lat/lng of an audio track pin (admin only).
 *
 * @param {string} trackId - Track UUID
 * @param {number} lat - New latitude
 * @param {number} lng - New longitude
 * @param {string} accessToken - JWT access token
 * @returns {Promise}
 */
export const updateTrackLocation = async (trackId, lat, lng, accessToken) => {
  const response = await axios.patch(
    `${apiAddress}/audio/tracks/${trackId}/location`,
    { lat, lng },
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  return response.data;
};
